import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  FieldIssue,
  CallOutcomeEnum,
  pick,
  TimezoneAbbreviationEnum,
  SetRequiredNonNullable,
  PoolBrainCompany,
  PoolBrainCustomerLocationDTO
} from '_lib/api/apiTypes.shim';

import { RootState } from '_lib/redux/store';
import { DateUtils } from '_lib/utils/date.utils';
import { getTradeForNewCompany } from '_lib/utils/helpers';
import { ClearIssueItem, removeIssues } from '_lib/utils/validationErrorUtils';

import type { SharedPhoneCallContext, SwitchProviderCompanyPayload } from '_lib/types/PhoneCall.types';
import type { RequireAtLeastOne } from 'type-fest';

export interface PhoneCallContext
  extends Pick<
    SharedPhoneCallContext,
    | 'callOutcome'
    | 'notes'
    | 'internalNotes'
    | 'sendEscalationEmail'
    | 'sendCompanyEmail'
    | 'serviceType'
    | 'trade'
    | 'appointmentDateTime'
  > {
  previousAppointmentDateTime: string | null;
  overridePreviousAppointmentDateTime: boolean;
  /** Customer for the call */
  customerLocation: PoolBrainCustomerLocationDTO | null;
  /** timezone of the company for the current call */
  companyTimezone: TimezoneAbbreviationEnum;
}

export const toSharedPhoneCallContext = (phoneCallContext: PhoneCallContext): SharedPhoneCallContext => {
  const { companyTimezone, callOutcome, previousAppointmentDateTime } = phoneCallContext;

  let previousAppointmentDate: string | null = null;
  if (callOutcome === CallOutcomeEnum.Reschedule && previousAppointmentDateTime) {
    previousAppointmentDate = DateUtils.toDateString(previousAppointmentDateTime, { timezone: companyTimezone });
  }

  return {
    callOutcome,
    previousAppointmentDate,
    internalNotes: phoneCallContext.internalNotes,
    notes: phoneCallContext.notes,
    sendCompanyEmail: phoneCallContext.sendCompanyEmail,
    sendEscalationEmail: phoneCallContext.sendEscalationEmail,
    trade: phoneCallContext.trade,
    serviceType: phoneCallContext.serviceType,
    appointmentDateTime: phoneCallContext.appointmentDateTime
  };
};

/* Subset of data that we can safely carry over upon a re-initialization from error */
const safeDataKeys = [
  'callOutcome',
  'notes',
  'internalNotes',
  'sendEscalationEmail',
  'sendCompanyEmail',
  'serviceType',
  'trade',
  'appointmentDateTime',
  'previousAppointmentDateTime'
] as const satisfies (keyof PhoneCallContext)[];

type InitialPhoneCallContext = SetRequiredNonNullable<Partial<PhoneCallContext>, 'companyTimezone'>;

const initPhoneCallContext = (ctx: InitialPhoneCallContext): PhoneCallContext => ({
  callOutcome: ctx.callOutcome,
  internalNotes: ctx.internalNotes || '',
  notes: ctx.notes || '',
  sendEscalationEmail: ctx.sendEscalationEmail !== undefined ? ctx.sendEscalationEmail : false,
  sendCompanyEmail: ctx.sendCompanyEmail !== undefined ? ctx.sendCompanyEmail : false,
  serviceType: ctx.serviceType || null,
  trade: ctx.trade || null,
  appointmentDateTime: ctx.appointmentDateTime || null,
  previousAppointmentDateTime: ctx.previousAppointmentDateTime || null,
  companyTimezone: ctx.companyTimezone,
  overridePreviousAppointmentDateTime: !!ctx?.overridePreviousAppointmentDateTime,
  customerLocation: ctx.customerLocation || null
});

interface PoolBrainPhoneCallSlice {
  phoneCallContext: PhoneCallContext;
  phoneCallFieldIssues: FieldIssue[];
  phoneCallNonFieldErrors: string[];
}

const initState = (phoneCallContext: InitialPhoneCallContext): PoolBrainPhoneCallSlice => ({
  phoneCallContext: initPhoneCallContext(phoneCallContext),
  phoneCallFieldIssues: [],
  phoneCallNonFieldErrors: []
});

export const poolBrainPhoneCallSlice = createSlice({
  name: 'poolBrainPhoneCallSlice',
  initialState: initState({ companyTimezone: TimezoneAbbreviationEnum.EST }),
  reducers: {
    initialize: (
      state,
      action: PayloadAction<{ company: PoolBrainCompany; options?: { keepExistingCallData: boolean } }>
    ) => {
      const { company, options } = action.payload;

      const trade = getTradeForNewCompany(
        (options?.keepExistingCallData && state.phoneCallContext.trade) || null,
        company
      );

      return initState({
        ...(options?.keepExistingCallData ? pick(state.phoneCallContext, safeDataKeys) : {}),
        companyTimezone: company.timezone,
        trade
      });
    },
    switchDifferentProviderCompany: (_state, action: PayloadAction<SwitchProviderCompanyPayload>) => {
      const { company, ...phoneCallContext } = action.payload || {};
      return initState({
        ...phoneCallContext,
        companyTimezone: company.timezone,
        trade: getTradeForNewCompany(phoneCallContext.trade, company)
      });
    },
    /** Used to ensure we have a valid trade selected when switching companies */
    normalizeTradeFromCompany: (state, action: PayloadAction<PoolBrainCompany>) => {
      state.phoneCallContext = {
        ...state.phoneCallContext,
        trade: getTradeForNewCompany(state.phoneCallContext.trade, action.payload)
      };
    },
    setPartialPhoneCallContext(
      state,
      action: PayloadAction<{
        updates: RequireAtLeastOne<Partial<PhoneCallContext>>;
        clearIssues?: ClearIssueItem[];
      }>
    ) {
      state.phoneCallContext = { ...state.phoneCallContext, ...action.payload.updates };
      if (state.phoneCallFieldIssues.length && action.payload.clearIssues?.length) {
        const updatedIssues = action.payload.clearIssues.reduce(
          (acc, item) => removeIssues({ ...(typeof item === 'string' ? { issuePath: item } : item), issues: acc }),
          state.phoneCallFieldIssues
        );
        if (updatedIssues.length !== state.phoneCallFieldIssues.length) state.phoneCallFieldIssues = updatedIssues;
      }
    },
    setPhoneCallFieldIssues(state, action: PayloadAction<FieldIssue[]>) {
      state.phoneCallFieldIssues = action.payload;
    },
    setPhoneCallNonFieldErrors(state, action: PayloadAction<string[]>) {
      state.phoneCallNonFieldErrors = action.payload;
    },
    reset: () => initState({ companyTimezone: TimezoneAbbreviationEnum.EST })
  },
  selectors: {
    selectPhoneCallContext: (state) => state.phoneCallContext,
    selectPhoneCallFieldIssues: (state) => state.phoneCallFieldIssues,
    selectPhoneCallNonFieldErrors: (state) => state.phoneCallNonFieldErrors
  }
});

export const {
  setPartialPhoneCallContext,
  setPhoneCallFieldIssues,
  setPhoneCallNonFieldErrors,
  normalizeTradeFromCompany,
  initialize,
  switchDifferentProviderCompany,
  reset
} = poolBrainPhoneCallSlice.actions;

export const { selectPhoneCallContext, selectPhoneCallFieldIssues, selectPhoneCallNonFieldErrors } =
  poolBrainPhoneCallSlice.getSelectors((rootState: RootState) => rootState.poolBrainPhoneCallSlice);
